/**
 * useVoiceInput Hook
 * Uses the Web Speech API for speech-to-text functionality
 * 
 * Features:
 * - Browser-native speech recognition (no backend needed)
 * - Real-time transcription
 * - Automatic stop on silence
 * - Error handling for unsupported browsers
 */

import { useState, useEffect, useCallback, useRef } from 'react';

// Check for browser support
const SpeechRecognition = typeof window !== 'undefined' 
  ? window.SpeechRecognition || window.webkitSpeechRecognition 
  : null;

export function useVoiceInput(options = {}) {
  const {
    onResult,           // Callback when speech is recognized
    onError,            // Callback for errors
    continuous = true,  // Keep listening after result (default: true for multi-item add)
    language = 'en-GB', // Language for recognition
  } = options;

  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState(null);
  
  const recognitionRef = useRef(null);
  const onResultRef = useRef(onResult);
  const onErrorRef = useRef(onError);
  const shouldRestartRef = useRef(false); // Track if we should auto-restart

  // Keep refs up to date
  useEffect(() => {
    onResultRef.current = onResult;
    onErrorRef.current = onError;
  }, [onResult, onError]);

  // Check browser support on mount
  useEffect(() => {
    setIsSupported(!!SpeechRecognition);
  }, []);

  // Initialize recognition
  useEffect(() => {
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = true; // Always use continuous mode
    recognition.interimResults = true;
    recognition.lang = language;

    recognition.onstart = () => {
      setIsListening(true);
      setError(null);
    };

    recognition.onend = () => {
      // Auto-restart if we should keep listening
      if (shouldRestartRef.current && continuous) {
        try {
          setTimeout(() => {
            if (shouldRestartRef.current) {
              recognition.start();
            }
          }, 100); // Small delay before restart
        } catch (err) {
          // Ignore restart errors
        }
      } else {
        setIsListening(false);
      }
    };

    recognition.onerror = (event) => {
      let errorMessage = 'Speech recognition error';
      let shouldStop = true;
      
      switch (event.error) {
        case 'no-speech':
          // Don't show error for no-speech in continuous mode, just keep listening
          if (continuous && shouldRestartRef.current) {
            shouldStop = false;
          } else {
            errorMessage = 'No speech detected. Please try again.';
          }
          break;
        case 'audio-capture':
          errorMessage = 'No microphone found. Please check your device.';
          break;
        case 'not-allowed':
          errorMessage = 'Microphone access denied. Please allow microphone access.';
          break;
        case 'network':
          errorMessage = 'Network error. Please check your connection.';
          break;
        case 'aborted':
          // User stopped listening, not an error
          shouldStop = true;
          errorMessage = null;
          break;
        default:
          errorMessage = `Error: ${event.error}`;
      }
      
      if (shouldStop) {
        shouldRestartRef.current = false;
        setIsListening(false);
      }
      
      if (errorMessage) {
        setError(errorMessage);
        onErrorRef.current?.(errorMessage);
      }
    };

    recognition.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalTranscript += result[0].transcript;
        } else {
          interimTranscript += result[0].transcript;
        }
      }

      const currentTranscript = finalTranscript || interimTranscript;
      setTranscript(currentTranscript);

      // Call onResult with final transcript
      if (finalTranscript) {
        onResultRef.current?.(finalTranscript.trim());
        setTranscript(''); // Clear transcript for next item
      }
    };

    recognitionRef.current = recognition;

    return () => {
      shouldRestartRef.current = false;
      recognition.abort();
    };
  }, [continuous, language]);

  // Start listening
  const startListening = useCallback(() => {
    if (!recognitionRef.current) {
      setError('Speech recognition not supported in this browser');
      return;
    }

    setTranscript('');
    setError(null);
    shouldRestartRef.current = true; // Enable auto-restart
    
    try {
      recognitionRef.current.start();
    } catch (err) {
      // Already started, ignore
      if (err.name !== 'InvalidStateError') {
        console.error('Speech recognition start error:', err);
        setError('Failed to start speech recognition');
        shouldRestartRef.current = false;
      }
    }
  }, []);

  // Stop listening
  const stopListening = useCallback(() => {
    shouldRestartRef.current = false; // Disable auto-restart
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsListening(false);
  }, []);

  // Toggle listening
  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  return {
    isListening,
    isSupported,
    transcript,
    error,
    startListening,
    stopListening,
    toggleListening,
  };
}

export default useVoiceInput;
